import { ButtonType } from '@/components/CmgButton/cmg-button';
import { formatOfferEndsDate } from '@/helpers';
import { Campaign, CampaignTypes, CustomerProfile } from '@/models';
import { Routes } from '@/router';
import { ExtraOptinEvent } from '@/services/analytics/events/extra-optin-event';
import { AnalyticsEventType } from '@/services/analytics/platforms/adobe-analytics/models';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

@Component({
  name: 'CmgRewardExtrasCampaignDetailModal',
})
export default class CmgRewardExtrasCampaignDetailModal extends Vue {
  isSubmitted: boolean = false;

  get campaign(): Campaign {
    return this.$appStore.state.ui.modals.rewardExtrasCampaignDetail.args!.campaign;
  }

  get modalWidth(): string {
    return '30rem';
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.rewardExtrasCampaignDetail.visible;
  }

  get isExtra(): boolean {
    return this.campaign.campaignType === CampaignTypes.Extra;
  }

  get isOptinExtra(): boolean {
    return this.campaign.campaignType?.toLowerCase() === CampaignTypes.OptIn;
  }

  get isOptedIn(): boolean {
    return this.campaign.isOptedIn;
  }

  get checkMarkUrl(): string {
    return this.$appStore.state.cms.images.icons.activatedCheckmark.default;
  }

  get expirationDate(): string {
    if (this.campaign.endDate) {
      return formatOfferEndsDate(new Date(this.campaign.endDate));
    }

    return '';
  }

  get expirationText(): string {
    return `Offer ends ${this.expirationDate}`;
  }

  get ctaText(): string {
    if (this.isOptinExtra && !this.isOptedIn) {
      return 'ACTIVATE';
    } else if (this.campaign.ctaText && this.campaign.ctaText.length > 0) {
      return this.campaign.ctaText;
    } else if (this.campaign.ctaType?.toLowerCase() == 'grouporder') {
      return (!this.campaign.ctaText || this.campaign.ctaText.length === 0) ?  `START YOUR GROUP ORDER` : this.campaign.ctaText;
    } else if (this.campaign.ctaType?.toLowerCase() == 'order') {
      return (!this.campaign.ctaText || this.campaign.ctaText.length === 0) ?  `START YOUR ORDER` : this.campaign.ctaText;
    }

    return 'START YOUR ORDER';
  }

  get moreOrders(): string {
    const stepsNeeded = this.campaign.totalNumberOfSteps - this.campaign.currentStep;
    return stepsNeeded + ' more ' + (stepsNeeded > 1?  'orders' : 'order');
  }

  get backgroundImageUrl(): string {
    return 'url(\'' + this.campaign.mediaUri + '\')';
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  onClose() {
    this.$services.ui.setModalVisibility('rewardExtrasCampaignDetail', false);
  }

  onClickTermsAndConditions() {
    const campaign = this.campaign;
    const onBack = () => {
      this.$services.ui.setModalVisibility('rewardExtrasCampaignDetail', true, { campaign });
      this.$services.ui.setModalVisibility('rewardTermsAndConditions', false);
    };

    this.$services.ui.setModalVisibility('rewardTermsAndConditions', true, { termsAndConditions: this.campaign.terms, onBack });
    this.$services.ui.setModalVisibility('rewardExtrasCampaignDetail', false);
  }

  onClickProgramTerms() {
    this.addLinkClickedEvent();
    window.open('/about-us/rewards-terms', '_blank');
  }

  onClickCta() {
    if (this.campaign.ctaUrl && this.campaign.ctaUrl.length > 0) {
      window.location.href = this.campaign.ctaUrl;
      return;
    }

    if (this.isOptinExtra && !this.isOptedIn) {
      this.isSubmitted = true;
      let myCampaign = this.campaign;
      
      this.$services.reward.optIntoCampaign(this.campaign.id).then(async data => {
        if(data?.awarded) {
          myCampaign.isOptedIn = true;

          if (!this.customer?.optedIntoExtras) {
            await this.$services.customer.updateCustomer({
              optedIntoExtras: true
            });
            this.customer!.optedIntoExtras = true;
          }

          this.$services.analytics.send(new ExtraOptinEvent(AnalyticsEventType.ActivateOffer, myCampaign, false));
        }
      })
      .catch(error => {
        const onClose = () => {
          this.$services.ui.setModalVisibility('basicError', false);
        };

        this.$services.ui.setModalVisibility('basicError', true, {
          header: `OUR BAD`,
          content: 'There was a problem opting into your extra. Give it a sec and try again.',
          background: 'white',
          options: [
            {
              text: 'Okay',
              type: ButtonType.Primary,
              onClick: onClose,
            },
          ],
          onClose,
        });
      })
      .finally(() => {
        this.isSubmitted = false;
        return;
      })
    } else {
      this.addLinkClickedEvent();

      switch (this.campaign.ctaType) {
        case 'GroupOrder':
          this.$router.push({ name: Routes.GroupOrderCreate });
          break;
        case 'Order':
          this.$router.push({ name: Routes.Order, hash: '#menu' });
          break;
        default:
          this.$router.push({ name: Routes.Order, hash: '#menu' });
          break;
      }
  
      this.onClose();
    }
  }

  addLinkClickedEvent() {
    this.$services.analytics.addLinkClickedEventToStore({
      linkClicks: { value: 1 },
      linkSection: 'rewards-extra',
      linkTitle: `rewardsExtra - ${this.campaign.title.toUpperCase()}`,
      name:  this.ctaText,            
      type: 'other',
    }, '');
  }
}